import { useCallback } from 'react'
import { useAtom, useSetAtom } from 'jotai'
import { cartAtom, cartEditedAtom, type CartItems } from './cart.store'
import { analytics } from '@/core/analytics/analytics'

/**
 * Pure V1 cart mutations. The generic hook lives in `default/cart`
 * so V1 countries (Thailand, Singapore) can reuse it.
 *
 * Country-specific behaviour is layered through the optional callbacks:
 *
 *   - `onAfterChange` — fires after a V1 cart write; receives the new
 *     cart and a JSON string of `shipment_packs` ready to be sent to
 *     `hot/format` style endpoints.
 *   - `canIncrease`   — guard run *only* on operations that grow total
 *     cart qty. Returning `false` blocks the mutation and skips the
 *     callback.
 *
 * Oman (V2) manages its own cart mutations via `useOmanCartActions`.
 */
export interface CartMutationOptions {
    onAfterChange?: (newCart: CartItems, packsJson: string) => void
    canIncrease?: (newCart: CartItems) => boolean
}

/** Which line the write is about — tracked so a support trace shows what the user clicked. */
interface CartChange {
    purchaseType: string
    flavor: string
    qty: number
}

const totalQty = (cart: CartItems): number => {
    let total = 0
    for (const flavors of Object.values(cart)) {
        for (const qty of Object.values(flavors)) total += qty
    }
    return total
}

export const useCartMutations = (options: CartMutationOptions = {}) => {
    const [cart, setCart] = useAtom(cartAtom)
    const setCartEdited = useSetAtom(cartEditedAtom)
    const { onAfterChange, canIncrease } = options

    // Shared write path for V1 mutations. Returns `true` when the write
    // was applied so callers (e.g. format-item handlers tracking their own
    // local adjustments) can skip side state when the change was blocked.
    const applyV1 = useCallback(
        (newCart: CartItems, change: CartChange): boolean => {
            const props = { purchase_type: change.purchaseType, flavor: change.flavor, qty: change.qty }
            if (canIncrease && totalQty(newCart) > totalQty(cart) && !canIncrease(newCart)) {
                // Silently refused before this: the shopper sees an error and nothing else does.
                analytics.track('cartLimitBlocked', props)
                return false
            }
            setCart(newCart)
            setCartEdited(true)
            onAfterChange?.(newCart, JSON.stringify(newCart))
            analytics.track(change.qty <= 0 ? 'cartItemRemoved' : 'cartItemQuantityChanged', props)
            return true
        },
        [cart, setCart, setCartEdited, canIncrease, onAfterChange]
    )

    const increaseV1Qty = useCallback(
        (purchaseType: string, flavor: string): boolean => {
            const currentQty = cart[purchaseType]?.[flavor] ?? 0
            return applyV1(
                {
                    ...cart,
                    [purchaseType]: { ...cart[purchaseType], [flavor]: currentQty + 1 }
                },
                { purchaseType, flavor, qty: currentQty + 1 }
            )
        },
        [cart, applyV1]
    )

    const decreaseV1Qty = useCallback(
        (purchaseType: string, flavor: string): boolean => {
            const currentQty = cart[purchaseType]?.[flavor] ?? 0
            if (currentQty <= 1) return false
            return applyV1(
                {
                    ...cart,
                    [purchaseType]: { ...cart[purchaseType], [flavor]: currentQty - 1 }
                },
                { purchaseType, flavor, qty: currentQty - 1 }
            )
        },
        [cart, applyV1]
    )

    const setV1Qty = useCallback(
        (purchaseType: string, flavor: string, qty: number): boolean => {
            if (qty < 1) return false
            return applyV1(
                {
                    ...cart,
                    [purchaseType]: { ...cart[purchaseType], [flavor]: qty }
                },
                { purchaseType, flavor, qty }
            )
        },
        [cart, applyV1]
    )

    // Mirrors legacy U-Shop's bin-icon behaviour: drop the line, prune
    // empty purchase-type buckets, then push the resulting cart through
    // the shared write path so any country sync runs once.
    const removeV1Entry = useCallback(
        (purchaseType: string, flavor: string): boolean => {
            const bucket = cart[purchaseType]
            if (!bucket || !(flavor in bucket)) return false

            const nextBucket = { ...bucket }
            delete nextBucket[flavor]

            const newCart: CartItems = { ...cart }
            if (Object.keys(nextBucket).length === 0) {
                delete newCart[purchaseType]
            } else {
                newCart[purchaseType] = nextBucket
            }

            return applyV1(newCart, { purchaseType, flavor, qty: 0 })
        },
        [cart, applyV1]
    )

    return {
        increaseV1Qty,
        decreaseV1Qty,
        setV1Qty,
        removeV1Entry
    }
}
